import { createSlice } from "@reduxjs/toolkit";

// Initial state factory
// Note for tests: import and use createInitialFormState() in assertions
// instead of duplicating the object shape in tests. This keeps tests in sync
// when defaults evolve.
export const createInitialFormState = () => ({
  selectedWorksheet: 0,
  sippsLevel:null,
  sippsLesson:null,
  includesReview: true,
  includeComprehension: false,
  includeImages: false,
  includeParagraphNumbers: true,
  textImages: [],
  anchorID: 0,
  textID: 0,
  quizID: 0,
  formType: null,
  // IMPORTANT: formType is the single source of truth for the selected tool
  // (e.g., 'assessment', 'reading', 'grading', 'sipps', etc).
  // Do NOT mirror this inside formData (e.g., as formData.tool). Historically
  // some components set formData.tool, but that is redundant and has been removed.
  formData: {
    text: "",
    image_instructions: "",
    grade: null,
    genre: null,
    length: null,
    image_speed: "fast"
  },
  exportedText: "",
  writingPrompt: {
    content:
      "The surprise fall of the Berlin wall in 1989 was a great event in the history of the world. It was a victory for freedom-seeking people",
    edited: false,
  },
  readingPassage: "",
  mcQuestions: [
    {
      question_id: "0.1",
      question: "What is the capital of Netherlands?",
      options: {
        A: "Amsterdam",
        B: "Rotterdam",
        C: "The Hague",
        D: "Utrecht",
      },
      answer: "A",
      reference: "https://en.wikipedia.org/wiki/Amsterdam",
      selected: true,
      deleted: false,
      edited: false,
    },
    {
      question_id: "0.2",
      question: "What is the capital of Egypt?",
      options: {
        A: "Cairo",
        B: "Alexandria",
        C: "Giza",
        D: "Luxor",
      },
      answer: "A",
      reference: "https://en.wikipedia.org/wiki/Cairo",
      selected: true,
      deleted: false,
      edited: false,
    },
    {
      question_id: "0.3",
      question: "What is the capital of Italy?",
      options: {
        A: "Rome",
        B: "Milan",
        C: "Naples",
        D: "Turin",
      },
      answer: "A",
      reference: "https://en.wikipedia.org/wiki/Rome",
      selected: true,
      deleted: false,
      edited: false,
    },
  ],
  ffQuestions: [
    {
      question_id: "0.1",
      question: "what do you think about the new education policy?",
      reference: "https://en.wikipedia.org/wiki/",
      answer: "I think it is a great step towards improving the education system.",
      selected: true,
      deleted: false,
      edited: false,
    },
    {
      question_id: "0.2",
      question: "list the advantages of the new education policy?",
      reference: "https://en.wikipedia.org/wiki/",
      answer: "The new education policy has several advantages such as flexibility in curriculum, emphasis on skill development, and integration of technology in education.",
      selected: true,
      deleted: false,
      edited: false,
    },
  ],
  tfQuestions: [
    {question_id: "0.1",
      question: "Is this a sample Question?",
      options: {
        True: "True",
        False: "False"
      },
      answer: "True",
      selected: true,
      deleted: false,
      edited: false
    }
  ],
  fbQuestions: [
    {
      question_id: "0.1",
      question: "This is a test _______",
      reference: "https://en.wikipedia.org/wiki/",
      answer: "question",
      selected: true,
      deleted: false,
      edited: false,
    },
  ],

});

const formSlice = createSlice({
  name: "form",
  // Use a fresh object to avoid accidental shared references
  initialState: createInitialFormState(),
  reducers: {
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
    setFormType: (state, action) => {
      const { formType } = action.payload;
      state.formType = formType;
    },

    deleteMcQuestion: (state, action) => {
      const question = action.payload;
      state.mcQuestions = state.mcQuestions.map((mcQuestion) =>
        mcQuestion.question_id === question.question_id
          ? action.payload
          : mcQuestion
      );
    },
    deleteFfQuestion: (state, action) => {
      const question = action.payload;
      state.ffQuestions = state.ffQuestions.map((ffQuestion) =>
        ffQuestion.question_id === question.question_id
          ? action.payload
          : ffQuestion
      );
    },
    deleteTfQuestion: (state, action) => {
      const question = action.payload;
      state.tfQuestions = state.tfQuestions.map((tfQuestion) =>
        tfQuestion.question_id === question.question_id
          ? action.payload
          : tfQuestion
      );
    },
    deleteFbQuestion: (state, action) => {
      const question = action.payload;
      state.fbQuestions = state.fbQuestions.map((fbQuestion) =>
        fbQuestion.question_id === question.question_id
          ? action.payload
          : fbQuestion
      );
    },

    editMcQuestion: (state, action) => {
      const { question_id, updatedQuestion, options, answer } = action.payload;
      state.mcQuestions = state.mcQuestions.map((mcQuestion) => {
        return mcQuestion.question_id === question_id
          ? {
              ...mcQuestion,
              edited: true,
              ...(updatedQuestion ? { question: updatedQuestion } : {}),
              ...(options ? { options } : {}),
              ...(answer ? { answer } : {}),
            }
          : mcQuestion;
      });
    },

    editFfQuestion: (state, action) => {
      const { question_id, updatedQuestion, answer } = action.payload;
      state.ffQuestions = state.ffQuestions.map((ffQuestion) => {
        return ffQuestion.question_id === question_id
          ? {
              ...ffQuestion,
              edited: true,
              ...(updatedQuestion ? { question: updatedQuestion } : {}),
              ...(answer ? { answer } : {}),
            }
          : ffQuestion;
      });
    },
    editTfQuestion: (state, action) => {
      const { question_id, updatedQuestion, options, answer } = action.payload;
      state.tfQuestions = state.tfQuestions.map((tfQuestion) => {
        return tfQuestion.question_id === question_id
          ? {
              ...tfQuestion,
              edited: true,
              ...(updatedQuestion ? { question: updatedQuestion } : {}),
              ...(options ? { options } : {}),
              ...(answer ? { answer } : {}),
            }
          : tfQuestion;
      });
    },
    editFbQuestion: (state, action) => {
      const { question_id, updatedQuestion, answer } = action.payload;
      state.fbQuestions = state.fbQuestions.map((fbQuestion) => {
        return fbQuestion.question_id === question_id
          ? {
              ...fbQuestion,
              edited: true,
              ...(updatedQuestion ? { question: updatedQuestion } : {}),
              ...(answer ? { answer } : {}),
            }
          : fbQuestion;
      });
    },

    setFormData: (state, action) => {
      const newFormData = action.payload;

      state.formData = { ...state.formData, ...newFormData };
    },
    setWritingPrompt: (state, action) => {
      const writingPrompt = action.payload;
      state.writingPrompt = writingPrompt;
    },
    setReadingPassage: (state, action) => {
      const readingPassage = action.payload;
      state.readingPassage = readingPassage;
    },
    setAnchorID: (state, action) => {
      const anchorID = action.payload;
      state.anchorID = anchorID;
    },
    setTextID: (state, action) => {
      const textID = action.payload;
      state.textID = textID;
    },
    setQuizID: (state, action) => {
      const quizID = action.payload;
      state.quizID = quizID;
    },
    setMcQuestions: (state, action) => {
      const mcQuestions = action.payload;
      state.mcQuestions = mcQuestions;
    },
    setFfQuestions: (state, action) => {
      const ffQuestions = action.payload;
      state.ffQuestions = ffQuestions;
    },
    setTfQuestions: (state, action) => {
      const tfQuestions = action.payload;
      state.tfQuestions = tfQuestions;
    },
    setFbQuestions: (state, action) => {
      const fbQuestions = action.payload;
      state.fbQuestions = fbQuestions;
    },
    setDefaultValues: (state, action) => {
      // Return a fresh initial state object to reset the slice
      // Note: assigning to `state = ...` does not work in Immer; returning a new state does.
      return createInitialFormState();
    },
    setSelectedWorksheet: (state, action) => {
      state.selectedWorksheet = action.payload;
    },
    setSippsLevel: (state, action) => {
      state.sippsLevel = action.payload;
    },
    setSippsLesson: (state, action) => {
      state.sippsLesson = action.payload;
    },
    setIncludesReview: (state, action) => {
      state.includesReview = action.payload;
    },
    setIncludeComprehension: (state, action) => {
      state.includeComprehension = action.payload;
    },
    setIncludeImages: (state, action) => {
      state.includeImages = action.payload;
    },
    setIncludeParagraphNumbers: (state, action) => {
      state.includeParagraphNumbers = action.payload;
    },
    setTextImages: (state, action) => {
      state.textImages = action.payload;
    },
    setExportedText: (state, action) => {
      state.exportedText = action.payload;
    },
  },
});

export const {
  setFormType,
  setFormData,
  setWritingPrompt,
  setReadingPassage,
  setAnchorID,
  setTextID,
  setQuizID,
  setMcQuestions,
  setFfQuestions,
  setTfQuestions,
  setFbQuestions,
  deleteFfQuestion,
  deleteMcQuestion,
  deleteTfQuestion,
  deleteFbQuestion,
  editFfQuestion,
  editMcQuestion,
  editTfQuestion,
  editFbQuestion,
  setDefaultValues,
  setSelectedWorksheet,
  setSippsLevel,
  setSippsLesson,
  setIncludesReview,
  setIncludeComprehension,
  setIncludeImages,
  setIncludeParagraphNumbers,
  setTextImages,
  setExportedText
} = formSlice.actions;

export default formSlice.reducer;

export const selectFormType = (state) => state.form.formType;
export const selectFormData = (state) => state.form.formData;
export const selectWritingPrompt = (state) => state.form.writingPrompt;
export const selectMcQuestions = (state) => state.form.mcQuestions;
export const selectFfQuestions = (state) => state.form.ffQuestions;
export const selectTfQuestions = (state) => state.form.tfQuestions;
export const selectFbQuestions = (state) => state.form.fbQuestions;
export const selectReadingPassage = (state) => state.form.readingPassage;
export const selectAnchorID = (state) => state.form.anchorID;
export const selectTextID = (state) => state.form.textID;
export const selectQuizID = (state) => state.form.quizID;
export const selectSelectedWorksheet = (state) => state.form.selectedWorksheet;
export const selectSippsLevel = (state) => state.form.sippsLevel;
export const selectSippsLesson = (state) => state.form.sippsLesson;
export const selectIncludesReview = (state) => state.form.includesReview;
export const selectIncludeComprehension = (state) => state.form.includeComprehension;
export const selectIncludeImages = (state) => state.form.includeImages;
export const selectIncludeParagraphNumbers = (state) => state.form.includeParagraphNumbers;
export const selectTextImages = (state) => state.form.textImages;
export const selectExportedText = (state) => state.form.exportedText;


